// Katalogos publish — inscribe a category id into the shared registry.
//
// Two-input, four-output register() transaction:
//
//   inputs
//     [0] state UTXO at the registry contract (minting NFT + registry
//         category). Consumed and immediately re-created at output[0].
//     [1] user's DEA UTXO holding at least `feeDea` atomic units
//     [2+] user's plain BCH UTXOs for miner fee + change dust
//
//   outputs
//     [0] state UTXO back to registry (minting NFT preserved, same
//         commitment) — this is the covenant's own re-creation invariant
//     [1] entry NFT to the registry: IMMUTABLE, commitment = the target
//         category being registered (bare 32 bytes)
//     [2] fee to treasury: DEA of at least feeDea atomic units
//     [3+] BCH change + DEA change if we picked more than we spent
//
// Signing: user signs inputs 1..N with their local key (SignatureTemplate);
// input 0 unlocks via the covenant's register(targetCategory, oracleSig,
// oracleMessage) function.
//
// Fee derivation matches the covenant on-chain:
//   feeDea = ceil(satsPerUsd / deaRateSats)
// On chipnet dev the oracle is stubbed to $500 / BCH → satsPerUsd = 200,000;
// DEA rate is 867 sats/atomic → feeDea = 231.
//
// The chipnet dev oracle is baked in (see CHIPNET_DEV_ORACLE_PRIV_HEX) so
// the Publish button can sign a valid $1 quote without a running oracle
// service. Mainnet uses a real oracle service — not scoped here.

import artifact from './artifacts/KatalogosRegistryV1.json';
import type { Artifact } from 'cashscript';
import { Contract, ElectrumNetworkProvider, TransactionBuilder, SignatureTemplate } from 'cashscript';
import {
  secp256k1,
  sha256 as laSha256,
  encodeCashAddress,
  CashAddressType,
  CashAddressNetworkPrefix,
} from '@bitauth/libauth';
import type { Signer } from './signer-store';

const ART = artifact as unknown as Artifact;

// Chipnet KatalogosRegistryV1 — mirrors shells/app/src/core/katalogos.ts
// KATALOGOS_VERSIONS.chipnet[0]. Update in lockstep with that file.
const CHIPNET_REGISTRY = {
  contractAddress:            'bchtest:pwm0e4rnq0htx97g3sllrgld652w7ztxlnqlx7820kd3mhklk6v8vfchkntlm',
  contractTokenAddress:       'bchtest:rwm0e4rnq0htx97g3sllrgld652w7ztxlnqlx7820kd3mhklk6v8vmtth22xs',
  registryCategoryId:         '04841b26ef63a7954e26227a37d104c83a3a75c94f1f88e9822d47896f021eea',
  feeCategoryId:              '3c2337f504ad543518040c2d96477e473efaf8803aa010ce63e93c07996fd0c0',
  deaRateSats:                867n,
  oraclePubKeyHex:            '023eafff70c2d019cb430d4f769441320a133f70e6ca01a0fc30d30c8245f459dd',
  treasuryLockingBytecodeHex: '76a914a4098912179c55b53f08b5c499e398e69e56f00c88ac',
};

// Chipnet dev oracle key — matches KATALOGOS_ORACLE_DEV.ts in shells/app.
// Rotated only when the covenant is redeployed. Chipnet-only; mainnet uses
// a real oracle service.
const CHIPNET_DEV_ORACLE_PRIV_HEX = '3b3f2287ba3ac5e0ad6e87b6763672f423869c5322c85672e8317f449aba8198';
const CHIPNET_ORACLE_SATS_PER_USD = 200_000n;

const DUST = 1000n;
const MINER_FEE = 2500n;

function hexToBin(h: string): Uint8Array {
  const c = h.startsWith('0x') ? h.slice(2) : h;
  const out = new Uint8Array(c.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(c.slice(i * 2, i * 2 + 2), 16);
  return out;
}
function txidLE(hex: string): Uint8Array {
  const d = hexToBin(hex);
  const o = new Uint8Array(d.length);
  for (let i = 0; i < d.length; i++) o[i] = d[d.length - 1 - i];
  return o;
}

function encodeOracleMessage(satsPerUsd: bigint): Uint8Array {
  // 16-byte AnyHedge / ADR-0052 shape: timestamp(4) sequence(4)
  // contentSequence(4) price(4), all uint32-LE. Chipnet dev pins the
  // first three fields to 1; the covenant only reads bytes 12..16.
  const m = new Uint8Array(16);
  const dv = new DataView(m.buffer);
  dv.setUint32(0, 1, true);
  dv.setUint32(4, 1, true);
  dv.setUint32(8, 1, true);
  dv.setUint32(12, Number(satsPerUsd), true);
  return m;
}

function signOracleMessage(message: Uint8Array): Uint8Array {
  const priv = hexToBin(CHIPNET_DEV_ORACLE_PRIV_HEX);
  const sig = secp256k1.signMessageHashSchnorr(priv, laSha256.hash(message));
  if (typeof sig === 'string') throw new Error('oracle sign failed: ' + sig);
  return sig;
}

/** feeDea = ceil(satsPerUsd / deaRateSats). Matches the covenant's ceil-div. */
function feeDeaFor(satsPerUsd: bigint, deaRateSats: bigint): bigint {
  return (satsPerUsd + deaRateSats - 1n) / deaRateSats;
}

export interface PublishResult {
  txid: string;
  targetCategoryId: string;
  feeDea: bigint;
}

export class PublishError extends Error {
  constructor(message: string, readonly code:
    | 'no-state-utxo' | 'insufficient-dea' | 'insufficient-bch' | 'invalid-category' | 'other',
  ) {
    super(message);
    this.name = 'PublishError';
  }
}

// OP_RETURN metadata convention — the leanest cross-user link.
//
// v1 Katalogos stores only the category id in the entry NFT commitment,
// which is why other users can SEE new listings but can't build a claim
// tx (they lack rate/feeBps/founderLB). Rather than deploy a v2 covenant,
// each Publish tx appends a small OP_RETURN carrying:
//
//     "DVMB" || template(1) || rateSats(8 LE) || feeBps(2 LE) || founderPkh(20)
//
// Readers pull each entry's origin tx via Electrum, parse the OP_RETURN,
// and fold the params into KNOWN_MBAS. Trust model: OP_RETURN is in the
// SAME tx as the register() call, so the publisher who paid the $1 DEA
// fee also authored these params — bad-faith params are economically
// gated. Wallets can additionally verify by deriving the contract address
// from (categoryId, rate, feeBps, founderPkh) and checking that a live
// reservoir exists at that address (see registry.ts verifyDiscovered).
//
// Template byte reserves room for future siblings:
//   0x00 = ArtemisionMintFT / DeviantOpenMintFT (identical shapes)
//   0x01 = DeviantShareMBA (later — needs additional fields)
//   0x02 = DeviantOracleMBA (later)
export const OP_RETURN_MAGIC = 'DVMB';   // 4-byte ASCII
export const TEMPLATE_ARTEMISION_MINT_FT = 0x00;
export const TEMPLATE_DEVIANT_SHARE_MBA  = 0x01;
export const TEMPLATE_DEVIANT_ORACLE_MBA = 0x02;

export interface PublishMetadata {
  templateId: number;                  // TEMPLATE_ARTEMISION_MINT_FT | _SHARE_MBA | _ORACLE_MBA
  // Common when template is ArtemisionMintFT / OpenMintFT
  rateSats?: bigint;                    // sats per FT unit — Artemision + Share
  founderFeeBps?: bigint;               // 0..500 — Artemision + Oracle (Share uses feeBps)
  founderLockingBytecodeHex?: string;   // 25-byte P2PKH — we extract the 20-byte pkh
  // Share MBA extras (0x01)
  feeBps?: bigint;                      // 0..500 total fee for Share
  companySplitBps?: bigint;             // 0..10000 company's share of the fee
  companyLockingBytecodeHex?: string;   // 25-byte P2PKH — company treasury
  distributionLockingBytecodeHex?: string; // 25-byte P2PKH — distribution vault
  // Oracle MBA extras (0x02)
  oraclePkHex?: string;                 // 33-byte compressed pubkey
  // Optional display + icon chunks
  ticker?: string;                     // optional display ticker (ASCII, ≤ 8 bytes)
  name?: string;                       // optional display name    (ASCII, ≤ 32 bytes)
  iconSha256Hex?: string;              // optional icon hash — exactly 64 hex chars
  iconPath?: string;                   // optional URL — ASCII, ≤ 96 bytes
}

const TICKER_MAX_BYTES = 8;
const NAME_MAX_BYTES = 32;
const ICON_PATH_MAX_BYTES = 96;

function utf8Bytes(s: string): Uint8Array {
  return new TextEncoder().encode(s);
}
function trimToBytes(s: string, max: number): Uint8Array {
  const b = utf8Bytes(s);
  return b.length <= max ? b : b.slice(0, max);
}
function toHex(b: Uint8Array): string {
  let s = '0x'; for (const x of b) s += x.toString(16).padStart(2, '0'); return s;
}

function pkhFromLB(lbHex: string, tag: string): Uint8Array {
  const pkh = hexToBin(lbHex).slice(3, 23);
  if (pkh.length !== 20) throw new PublishError('bad ' + tag + ' locking bytecode: pkh not 20 bytes', 'other');
  return pkh;
}
function writeU64LE(dv: DataView, offset: number, v: bigint): void {
  const lo = Number(v & 0xffffffffn);
  const hi = Number((v >> 32n) & 0xffffffffn);
  dv.setUint32(offset, lo, true);
  dv.setUint32(offset + 4, hi, true);
}

function encodeMetadataBlob(m: PublishMetadata): string {
  // Variable-length payload per templateId. Byte 0 is always the template.
  // Readers dispatch on it — see registry.ts fetchEntryParams.
  //
  //   0x00 ArtemisionMintFT  · rate(8) fee(2) founderPkh(20)          = 31 bytes
  //   0x01 DeviantShareMBA   · rate(8) fee(2) split(2)
  //                             companyPkh(20) distributionPkh(20)     = 53 bytes
  //   0x02 DeviantOracleMBA  · fee(2) founderPkh(20) oraclePk(33)      = 56 bytes
  //
  // Total DVMB budget: 4 (magic) + up to 56 (blob) + up to 42 (label chunks) +
  // up to 128 (icon chunks) ≈ 230 bytes worst-case, ~10 bytes over the
  // standard 220-byte OP_RETURN cap. Chipnet Fulcrum + BCHN accept 300 bytes;
  // if a publisher hits the limit on mainnet, they drop the icon (still fine)
  // or the name chunk. All chunks past chunk 1 are optional.
  switch (m.templateId) {
    case TEMPLATE_ARTEMISION_MINT_FT: {
      if (m.rateSats == null || m.founderFeeBps == null || !m.founderLockingBytecodeHex) {
        throw new PublishError('ArtemisionMintFT DVMB needs rateSats + founderFeeBps + founderLockingBytecodeHex', 'other');
      }
      const buf = new Uint8Array(1 + 8 + 2 + 20);
      const dv = new DataView(buf.buffer);
      buf[0] = TEMPLATE_ARTEMISION_MINT_FT;
      writeU64LE(dv, 1, m.rateSats);
      dv.setUint16(9, Number(m.founderFeeBps & 0xffffn), true);
      buf.set(pkhFromLB(m.founderLockingBytecodeHex, 'founder'), 11);
      return '0x' + Array.from(buf, (b) => b.toString(16).padStart(2, '0')).join('');
    }
    case TEMPLATE_DEVIANT_SHARE_MBA: {
      if (m.rateSats == null || m.feeBps == null || m.companySplitBps == null ||
          !m.companyLockingBytecodeHex || !m.distributionLockingBytecodeHex) {
        throw new PublishError('DeviantShareMBA DVMB needs rate + fee + split + company/distribution LB', 'other');
      }
      const buf = new Uint8Array(1 + 8 + 2 + 2 + 20 + 20);
      const dv = new DataView(buf.buffer);
      buf[0] = TEMPLATE_DEVIANT_SHARE_MBA;
      writeU64LE(dv, 1, m.rateSats);
      dv.setUint16(9, Number(m.feeBps & 0xffffn), true);
      dv.setUint16(11, Number(m.companySplitBps & 0xffffn), true);
      buf.set(pkhFromLB(m.companyLockingBytecodeHex, 'company'), 13);
      buf.set(pkhFromLB(m.distributionLockingBytecodeHex, 'distribution'), 33);
      return '0x' + Array.from(buf, (b) => b.toString(16).padStart(2, '0')).join('');
    }
    case TEMPLATE_DEVIANT_ORACLE_MBA: {
      if (m.founderFeeBps == null || !m.founderLockingBytecodeHex || !m.oraclePkHex) {
        throw new PublishError('DeviantOracleMBA DVMB needs founderFeeBps + founderLockingBytecodeHex + oraclePkHex', 'other');
      }
      if (!/^[0-9a-f]{66}$/i.test(m.oraclePkHex)) throw new PublishError('oraclePkHex must be 66 hex chars (33-byte compressed)', 'other');
      const buf = new Uint8Array(1 + 2 + 20 + 33);
      const dv = new DataView(buf.buffer);
      buf[0] = TEMPLATE_DEVIANT_ORACLE_MBA;
      dv.setUint16(1, Number(m.founderFeeBps & 0xffffn), true);
      buf.set(pkhFromLB(m.founderLockingBytecodeHex, 'founder'), 3);
      buf.set(hexToBin(m.oraclePkHex), 23);
      return '0x' + Array.from(buf, (b) => b.toString(16).padStart(2, '0')).join('');
    }
    default:
      throw new PublishError('unknown templateId 0x' + m.templateId.toString(16), 'other');
  }
}

export async function publishViaLocalSigner(
  signer: Signer,
  targetCategoryId: string,
  metadata?: PublishMetadata,
): Promise<PublishResult> {
  if (!/^[0-9a-f]{64}$/i.test(targetCategoryId)) {
    throw new PublishError('categoryId must be 64 hex chars', 'invalid-category');
  }

  const provider = new ElectrumNetworkProvider('chipnet');

  // Reconstruct the covenant so we can spend its state UTXO. Constructor
  // args mirror shells/app/src/core/katalogos.ts.
  const contract = new Contract(
    ART,
    [
      txidLE(CHIPNET_REGISTRY.registryCategoryId),
      txidLE(CHIPNET_REGISTRY.feeCategoryId),
      CHIPNET_REGISTRY.deaRateSats,
      hexToBin(CHIPNET_REGISTRY.oraclePubKeyHex),
      hexToBin(CHIPNET_REGISTRY.treasuryLockingBytecodeHex),
    ],
    { provider },
  );
  // Sanity-check that the derived address matches the pinned one — a drift
  // in constructor args or artifact would silently spend to the wrong
  // contract and lose funds; better to fail loudly.
  if (contract.tokenAddress !== CHIPNET_REGISTRY.contractTokenAddress) {
    throw new PublishError(
      'contract token address drift: derived ' + contract.tokenAddress +
      ' vs pinned ' + CHIPNET_REGISTRY.contractTokenAddress,
      'other',
    );
  }

  // Find the state UTXO — the minting NFT of the registry category parked
  // at the contract's own token address.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const registryUtxos = (await provider.getUtxos(contract.tokenAddress)) as any[];
  const stateUtxo = registryUtxos.find((u) =>
    u.token?.category === CHIPNET_REGISTRY.registryCategoryId &&
    u.token?.nft?.capability === 'minting'
  );
  if (!stateUtxo) throw new PublishError('no state UTXO at registry — covenant may be draining', 'no-state-utxo');

  const satsPerUsd = CHIPNET_ORACLE_SATS_PER_USD;
  const feeDea = feeDeaFor(satsPerUsd, CHIPNET_REGISTRY.deaRateSats);

  // Pick DEA and BCH from the signer's UTXOs.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const own = (await provider.getUtxos(signer.cashaddr)) as any[];
  const deaUtxos = own.filter((u) => u.token?.category === CHIPNET_REGISTRY.feeCategoryId && !u.token?.nft);
  deaUtxos.sort((a, b) => Number(BigInt(b.token.amount) - BigInt(a.token.amount)));
  const pickedDea: typeof deaUtxos = []; let haveDea = 0n;
  for (const u of deaUtxos) {
    pickedDea.push(u); haveDea += BigInt(u.token.amount);
    if (haveDea >= feeDea) break;
  }
  if (haveDea < feeDea) {
    throw new PublishError(
      'insufficient DEA: need ' + feeDea + ' atomic units ($1 in DEA at chipnet dev-oracle rate), have ' + haveDea + '. ' +
      'The wallet holding this key needs a small amount of the chipnet DEA token — category ' +
      CHIPNET_REGISTRY.feeCategoryId.slice(0, 16) + '…',
      'insufficient-dea',
    );
  }

  const plainBch = own.filter((u) => !u.token).sort((a, b) => Number(BigInt(b.satoshis) - BigInt(a.satoshis)));
  const pickedBch: typeof plainBch = []; let haveBch = 0n;
  // Cover: three output dust (state + entry + fee), miner fee, DEA-change dust if any
  const bchNeed = 3n * DUST + MINER_FEE + DUST;
  for (const u of plainBch) {
    pickedBch.push(u); haveBch += BigInt(u.satoshis);
    if (haveBch >= bchNeed) break;
  }
  if (haveBch < bchNeed) {
    throw new PublishError('insufficient BCH: need ' + bchNeed + ' sats for miner fee + dust, have ' + haveBch, 'insufficient-bch');
  }

  // Sig template for the user's DEA + BCH inputs.
  const sig = new SignatureTemplate(signer.privateKey);

  // Oracle quote (chipnet dev — signed here in the browser with the baked key).
  const oracleMessage = encodeOracleMessage(satsPerUsd);
  const oracleSig = signOracleMessage(oracleMessage);

  const stateInputSats = BigInt(stateUtxo.satoshis);
  const deaChange = haveDea - feeDea;

  const tb = new TransactionBuilder({ provider });

  // Input 0 — state UTXO, unlocked via register().
  // The targetCategory arg must match what cashscript actually writes into
  // the NFT commitment field on output[1] (they're compared byte-for-byte
  // inside the covenant). cashscript writes the commitment hex STRING as-is
  // (display order), so we hand register() the same display-order bytes —
  // NOT the reversed consensus form (that's only correct for constructor
  // args like `registryCategory`, which reference the token category via the
  // OP_*TOKENCATEGORY opcodes that return consensus order).
  tb.addInput(stateUtxo, contract.unlock.register(hexToBin(targetCategoryId), oracleSig, oracleMessage));
  // Input 1+ — DEA UTXOs
  for (const u of pickedDea) tb.addInput(u, sig.unlockP2PKH());
  // Input N+ — plain BCH UTXOs
  for (const u of pickedBch) tb.addInput(u, sig.unlockP2PKH());

  // Output 0 — state re-created (minting NFT preserved)
  tb.addOutput({
    to: contract.tokenAddress,
    amount: stateInputSats >= DUST ? stateInputSats : DUST,
    token: {
      amount: BigInt(stateUtxo.token.amount ?? 0),
      category: CHIPNET_REGISTRY.registryCategoryId,
      nft: {
        capability: 'minting',
        commitment: stateUtxo.token.nft?.commitment ?? '',
      },
    },
  });

  // Output 1 — entry NFT: immutable, commitment = target category
  tb.addOutput({
    to: contract.tokenAddress,
    amount: DUST,
    token: {
      amount: 0n,
      category: CHIPNET_REGISTRY.registryCategoryId,
      nft: { capability: 'none', commitment: targetCategoryId },
    },
  });

  // Output 2 — fee to treasury: DEA at exactly feeDea (or more).
  tb.addOutput({
    to: bchtestP2pkhFromLB(CHIPNET_REGISTRY.treasuryLockingBytecodeHex),
    amount: DUST,
    token: { amount: feeDea, category: CHIPNET_REGISTRY.feeCategoryId },
  });

  // Output 3+ — DEA change (if any) back to signer
  if (deaChange > 0n) {
    tb.addOutput({
      to: signer.tokenaddr,
      amount: DUST,
      token: { amount: deaChange, category: CHIPNET_REGISTRY.feeCategoryId },
    });
  }

  // Output N — plain BCH change
  const totalInputBch = stateInputSats + pickedDea.reduce((s, u) => s + BigInt(u.satoshis), 0n) + haveBch;
  const totalOutputBch =
    (stateInputSats >= DUST ? stateInputSats : DUST) +   // out[0]
    DUST +                                                // out[1]
    DUST +                                                // out[2]
    (deaChange > 0n ? DUST : 0n);                         // out[3] if present
  const bchChange = totalInputBch - totalOutputBch - MINER_FEE;
  if (bchChange < DUST) {
    throw new PublishError('BCH change below dust: ' + bchChange, 'insufficient-bch');
  }
  tb.addOutput({ to: signer.cashaddr, amount: bchChange });

  // OP_RETURN metadata sidecar — carries the params other wallets need to
  // build a claim tx against `targetCategoryId`. 0 sats out, ~34 bytes,
  // negligible fee. See the OP_RETURN_MAGIC comment above for the rationale
  // and format. Omitting metadata is fine (backwards-compatible with v1);
  // the entry still lists, just not swap-ready cross-user.
  if (metadata) {
    const payload = encodeMetadataBlob(metadata);
    const chunks: string[] = [OP_RETURN_MAGIC, payload];
    // Optional chunks. Kept as separate pushes rather than packed into
    // the 31-byte blob so length is variable and any can be omitted;
    // readers that only understand chunks 1-2 still parse.
    //
    // Icon sidecar (chunks 5-6, ADR-0055 Amendment 2026-09-02):
    //   • iconSha256 is a 32-byte hash of the intended icon bytes. It
    //     LANDS ON-CHAIN — un-swappable, only the founder key can
    //     inscribe a new one via a fresh Publish tx.
    //   • iconPath is a plain ASCII Sia path (moderatable). Bytes are
    //     stored off-chain at that path; the reader fetches them and
    //     verifies against iconSha256 before rendering. On mismatch or
    //     unreachable, the wallet falls back to the symbol glyph — no
    //     silent icon swap possible.
    //
    // Order MUST stay ticker, name, iconSha256, iconPath so old readers
    // (which only look at chunks 2-3) keep working.
    if (metadata.ticker)       chunks.push(toHex(trimToBytes(metadata.ticker, TICKER_MAX_BYTES)));
    if (metadata.name)         chunks.push(toHex(trimToBytes(metadata.name,   NAME_MAX_BYTES)));
    if (metadata.iconSha256Hex) {
      if (!/^[0-9a-f]{64}$/i.test(metadata.iconSha256Hex)) throw new PublishError('iconSha256Hex must be 64 hex chars', 'other');
      chunks.push('0x' + metadata.iconSha256Hex.toLowerCase());
    }
    if (metadata.iconPath)     chunks.push(toHex(trimToBytes(metadata.iconPath, ICON_PATH_MAX_BYTES)));
    tb.addOpReturnOutput(chunks);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sent: any = await tb.send();
  const txid = typeof sent === 'string' ? sent : (sent.txid ?? sent.txId);
  return { txid, targetCategoryId, feeDea };
}

// Local helper — build a bchtest: P2PKH cash address from a 25-byte
// locking bytecode hex (treasury). Keeps the module self-contained.
function bchtestP2pkhFromLB(lbHex: string): string {
  // 25-byte P2PKH: 76 a9 14 <pkh 20> 88 ac
  const lb = hexToBin(lbHex);
  if (lb.length !== 25 || lb[0] !== 0x76 || lb[1] !== 0xa9 || lb[2] !== 0x14 || lb[23] !== 0x88 || lb[24] !== 0xac) {
    throw new Error('unexpected treasury locking bytecode shape');
  }
  const pkh = lb.slice(3, 23);
  // p2pkhWithTokens (bchtest:z…) — same P2PKH locking bytecode but the
  // token-aware address form, which cashscript's addOutput requires when a
  // token payload rides along. The covenant only checks lockingBytecode so
  // this is bytecode-equivalent to the plain P2PKH form the treasury uses.
  const res = encodeCashAddress({
    prefix: CashAddressNetworkPrefix.testnet,
    type: CashAddressType.p2pkhWithTokens,
    payload: pkh,
  });
  if (typeof res === 'string') return res;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (res as any).address ?? String(res);
}
